import { cn } from "@/lib/utils";
import { formatDuration } from "@/lib/format-utils";
import type { UserReportRow, UserReportTotals } from "@/hooks/queries/use-admin-activity";
import type { PeriodType } from "@/components/admin/PeriodSelector";

interface UserReportTableProps {
  rows: UserReportRow[];
  totals: UserReportTotals;
  periodType?: PeriodType;
}

// ── Date / period label formatting ──────────────────────────────────

function formatDayLabel(isoDate: string): string {
  const parts = isoDate.split("-");
  const year = parseInt(parts[0] ?? "2000", 10);
  const month = parseInt(parts[1] ?? "1", 10);
  const day = parseInt(parts[2] ?? "1", 10);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function formatWeekLabel(weekKey: string): string {
  // weekKey is the Monday date YYYY-MM-DD
  const parts = weekKey.split("-");
  const year = parseInt(parts[0] ?? "2000", 10);
  const month = parseInt(parts[1] ?? "1", 10);
  const day = parseInt(parts[2] ?? "1", 10);
  const monday = new Date(year, month - 1, day);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${fmt(monday)} – ${fmt(sunday)}`;
}

function formatMonthLabel(monthKey: string): string {
  // monthKey is YYYY-MM
  const [yearStr, monthStr] = monthKey.split("-");
  const d = new Date(parseInt(yearStr ?? "2000", 10), parseInt(monthStr ?? "1", 10) - 1, 1);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function formatPeriodLabel(dateKey: string, periodType?: PeriodType): string {
  if (periodType === "quarter") return formatWeekLabel(dateKey);
  if (periodType === "year") return formatMonthLabel(dateKey);
  return formatDayLabel(dateKey);
}

function periodColumnHeader(periodType?: PeriodType): string {
  if (periodType === "quarter") return "Week";
  if (periodType === "year") return "Month";
  return "Date";
}

// ── Column definitions ──────────────────────────────────────────────

type CountKey = "epicRequestsCreated" | "epicsCreated" | "featuresCompleted" | "tasksCompleted" | "epicsCompleted";

const countColumns: { key: CountKey; label: string }[] = [
  { key: "epicRequestsCreated", label: "Epic Requests" },
  { key: "epicsCreated", label: "Epics Created" },
  { key: "featuresCompleted", label: "Features Done" },
  { key: "tasksCompleted", label: "Tasks Done" },
  { key: "epicsCompleted", label: "Epics Done" },
];

type TimeKey = "totalDurationMinutes" | "avgDurationPerItem" | "aiSessionMinutes";

const timeColumns: { key: TimeKey; label: string }[] = [
  { key: "totalDurationMinutes", label: "Time Invested" },
  { key: "avgDurationPerItem", label: "Avg Time/Item" },
  { key: "aiSessionMinutes", label: "AI Session Time" },
];

// ── Cell components ─────────────────────────────────────────────────

function MetricCell({
  value,
  bold,
}: {
  value: number;
  bold?: boolean;
}) {
  return (
    <td
      className={cn(
        "px-4 py-2 text-right tabular-nums text-sm",
        value === 0 ? "text-muted-foreground" : "text-foreground",
        bold && "font-semibold"
      )}
    >
      {value === 0 ? "—" : value}
    </td>
  );
}

function TimeCell({
  minutes,
  bold,
}: {
  minutes: number;
  bold?: boolean;
}) {
  return (
    <td
      className={cn(
        "px-4 py-2 text-right tabular-nums text-sm",
        minutes === 0 ? "text-muted-foreground" : "text-foreground",
        bold && "font-semibold"
      )}
    >
      {minutes === 0 ? "—" : formatDuration(minutes)}
    </td>
  );
}

// ── Table component ─────────────────────────────────────────────────

export function UserReportTable({ rows, totals, periodType }: UserReportTableProps) {
  if (rows.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        No activity in this date range
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border/50">
            <th className="px-4 py-2 text-left font-medium text-muted-foreground">
              {periodColumnHeader(periodType)}
            </th>
            {countColumns.map((col) => (
              <th
                key={col.key}
                className="px-4 py-2 text-right font-medium text-muted-foreground whitespace-nowrap"
              >
                {col.label}
              </th>
            ))}
            {timeColumns.map((col) => (
              <th
                key={col.key}
                className="px-4 py-2 text-right font-medium text-muted-foreground whitespace-nowrap"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.date}
              className="border-b border-border/20 even:bg-muted/50 hover:bg-white/[0.02] transition-colors"
            >
              <td className="px-4 py-2 text-foreground whitespace-nowrap">
                {formatPeriodLabel(row.date, periodType)}
              </td>
              {countColumns.map((col) => (
                <MetricCell key={col.key} value={row[col.key]} />
              ))}
              {timeColumns.map((col) => (
                <TimeCell key={col.key} minutes={row[col.key]} />
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-muted/40 border-t border-border/50">
            <td className="px-4 py-2 font-semibold text-foreground">
              Total
            </td>
            {countColumns.map((col) => (
              <MetricCell key={col.key} value={totals[col.key]} bold />
            ))}
            {timeColumns.map((col) => (
              <TimeCell key={col.key} minutes={totals[col.key]} bold />
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
